/* ============================================================
   Store locator — Mapbox map + filterable partner list
   ============================================================ */
(function () {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function boot() {
    const shells = $$('[data-store-locator]');
    shells.forEach(initLocator);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function initLocator(shell) {
    const token = shell.dataset.token;
    const styleUrl = shell.dataset.mapStyle || 'mapbox://styles/mapbox/light-v11';
    const defaultCountry = (shell.dataset.defaultCountry || '').trim();
    const colorDealer = shell.dataset.markerDealer || '#EF6623';
    const colorDistributor = shell.dataset.markerDistributor || '#2C2A27';

    const listEl = $('[data-sl-list]', shell);
    const mapEl = $('[data-sl-map]', shell);
    const fallbackEl = $('[data-sl-map-fallback]', shell);
    const countEl = $('[data-sl-count]', shell);
    const searchEl = $('[data-sl-search]', shell);
    const countrySel = $('[data-sl-country]', shell);
    const typeBtns = $$('[data-sl-type]', shell);
    const locateBtn = $('[data-sl-locate]', shell);
    const viewBtns = $$('[data-sl-view]', shell);

    // ---- Collect partners from the server-rendered list ----
    const items = $$('[data-sl-item]', listEl);
    const partners = items.map((el) => {
      const lat = parseFloat(el.dataset.lat);
      const lng = parseFloat(el.dataset.lng);
      const addrEl = $('.sl-item-address', el);
      return {
        el,
        id: el.dataset.id,
        name: el.dataset.name || '',
        type: el.dataset.type || 'dealer',
        country: (el.dataset.country || '').trim(),
        city: el.dataset.city || '',
        address: addrEl ? addrEl.innerText.trim() : '',
        geocodeQuery: el.dataset.geocode || (el.dataset.name + ' ' + (el.dataset.country || '')),
        lat: isFinite(lat) ? lat : null,
        lng: isFinite(lng) ? lng : null,
        phone: (($('a[href^="tel:"]', el) || {}).getAttribute ? $('a[href^="tel:"]', el).getAttribute('href') : ''),
        email: (($('a[href^="mailto:"]', el) || {}).getAttribute ? $('a[href^="mailto:"]', el).getAttribute('href') : ''),
        website: (() => { const a = $('.sl-item-contact a[target="_blank"]:not([data-sl-directions])', el); return a ? a.getAttribute('href') : ''; })(),
        marker: null,
        popup: null,
      };
    });

    // ---- Country filter options ----
    const countries = Array.from(new Set(partners.map((p) => p.country).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b));
    countries.forEach((c) => {
      const opt = document.createElement('option');
      opt.value = c; opt.textContent = c;
      countrySel && countrySel.appendChild(opt);
    });
    if (defaultCountry && countrySel && countries.includes(defaultCountry)) {
      countrySel.value = defaultCountry;
    }

    // ---- Group the list by country (sticky headers) ----
    const groupHeads = {};
    (function groupList() {
      const byCountry = {};
      partners.forEach((p) => {
        const key = p.country || 'Other';
        (byCountry[key] = byCountry[key] || []).push(p);
      });
      const orderedKeys = Object.keys(byCountry).sort((a, b) => a.localeCompare(b));
      listEl.innerHTML = '';
      orderedKeys.forEach((key) => {
        const head = document.createElement('li');
        head.className = 'sl-group-head';
        head.textContent = key;
        head.dataset.slGroup = key;
        listEl.appendChild(head);
        groupHeads[key] = head;
        byCountry[key]
          .sort((a, b) => a.name.localeCompare(b.name))
          .forEach((p) => listEl.appendChild(p.el));
      });
    })();

    const state = { type: '', country: countrySel ? countrySel.value : '', search: '' };

    // ---- Map ----
    let map = null;
    let mapReady = false;
    const hasMapbox = !!(window.mapboxgl && token);

    function makeMarkerEl(p) {
      const el = document.createElement('div');
      el.className = 'sl-marker';
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', p.name);
      // Inner span holds the visible dot and keeps the div from matching the
      // theme's global `div:empty { display:none }` reset (spans are exempt).
      const dot = document.createElement('span');
      dot.style.background = p.type === 'distributor' ? colorDistributor : colorDealer;
      el.appendChild(dot);
      el.addEventListener('click', (e) => { e.stopPropagation(); focusPartner(p, { fromMarker: true }); });
      return el;
    }

    function popupHtml(p) {
      const typeLabel = p.type === 'distributor' ? 'Distributor' : 'Dealer';
      let links = '';
      if (p.phone) links += `<a href="${escapeHtml(p.phone)}">Call</a>`;
      if (p.email) links += `<a href="${escapeHtml(p.email)}">Email</a>`;
      if (p.website) links += `<a href="${escapeHtml(p.website)}" target="_blank" rel="noopener">Website</a>`;
      if (p.lat != null && p.lng != null) {
        links += `<a href="https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}" target="_blank" rel="noopener">Directions</a>`;
      }
      return `<div class="sl-popup">
        <p class="sl-popup-name">${escapeHtml(p.name)}</p>
        <p class="sl-popup-type" style="color:${p.type === 'distributor' ? colorDistributor : colorDealer}">${typeLabel}</p>
        ${p.address ? `<p class="sl-popup-addr">${escapeHtml(p.address).replace(/\n/g, '<br>')}</p>` : ''}
        <div class="sl-popup-links">${links}</div>
      </div>`;
    }

    function addMarker(p) {
      if (!mapReady || p.lat == null || p.lng == null || p.marker) return;
      p.popup = new mapboxgl.Popup({ offset: 18, closeButton: true }).setHTML(popupHtml(p));
      p.marker = new mapboxgl.Marker({ element: makeMarkerEl(p), anchor: 'bottom' })
        .setLngLat([p.lng, p.lat])
        .setPopup(p.popup)
        .addTo(map);
      // Keep the directions link in the list in sync
      const dir = $('[data-sl-directions]', p.el);
      if (dir) {
        dir.href = `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`;
        dir.hidden = false;
      }
    }

    function visiblePartners() {
      const q = state.search.toLowerCase();
      return partners.filter((p) => {
        if (state.type && p.type !== state.type) return false;
        if (state.country && p.country !== state.country) return false;
        if (q) {
          const hay = (p.name + ' ' + p.country + ' ' + p.city + ' ' + p.address).toLowerCase();
          if (hay.indexOf(q) === -1) return false;
        }
        return true;
      });
    }

    function applyFilters(fit) {
      const visible = new Set(visiblePartners());
      partners.forEach((p) => {
        const show = visible.has(p);
        p.el.hidden = !show;
        if (p.marker) p.marker.getElement().style.display = show ? '' : 'none';
      });
      // Group header visibility
      Object.keys(groupHeads).forEach((key) => {
        const anyVisible = partners.some((p) => (p.country || 'Other') === key && !p.el.hidden);
        groupHeads[key].hidden = !anyVisible;
      });
      if (countEl) {
        const n = visible.size;
        countEl.textContent = n === 1 ? '1 location' : n + ' locations';
      }
      if (fit) fitToVisible();
    }

    function fitToVisible() {
      if (!mapReady) return;
      const pts = visiblePartners().filter((p) => p.lat != null && p.lng != null);
      if (!pts.length) return;
      if (pts.length === 1) {
        map.flyTo({ center: [pts[0].lng, pts[0].lat], zoom: 10, duration: 600 });
        return;
      }
      const b = new mapboxgl.LngLatBounds();
      pts.forEach((p) => b.extend([p.lng, p.lat]));
      map.fitBounds(b, { padding: 60, maxZoom: 11, duration: 600 });
    }

    let activePartner = null;
    function focusPartner(p, opts) {
      opts = opts || {};
      if (activePartner && activePartner !== p) {
        activePartner.el.classList.remove('is-active');
        if (activePartner.marker) activePartner.marker.getElement().classList.remove('is-active');
      }
      activePartner = p;
      p.el.classList.add('is-active');
      if (p.marker) p.marker.getElement().classList.add('is-active');

      if (mapReady && p.lat != null && p.lng != null) {
        map.flyTo({ center: [p.lng, p.lat], zoom: Math.max(map.getZoom(), 9), duration: 700 });
        if (p.popup && p.marker && !p.popup.isOpen()) p.marker.togglePopup();
      }
      if (opts.fromMarker) {
        // Switch to list on mobile so the highlighted card is reachable
        p.el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    // Wire list item focus buttons
    partners.forEach((p) => {
      const btn = $('[data-sl-focus]', p.el);
      if (btn) btn.addEventListener('click', () => focusPartner(p));
    });

    // ---- Controls ----
    if (searchEl) {
      let t;
      searchEl.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => { state.search = searchEl.value.trim(); applyFilters(true); }, 180);
      });
    }
    if (countrySel) {
      countrySel.addEventListener('change', () => { state.country = countrySel.value; applyFilters(true); });
    }
    typeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        typeBtns.forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        state.type = btn.dataset.slType || '';
        applyFilters(true);
      });
    });
    if (locateBtn && 'geolocation' in navigator) {
      locateBtn.addEventListener('click', () => {
        locateBtn.disabled = true;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            locateBtn.disabled = false;
            const { latitude, longitude } = pos.coords;
            if (mapReady) {
              map.flyTo({ center: [longitude, latitude], zoom: 8, duration: 900 });
              new mapboxgl.Marker({ color: '#3b82f6' }).setLngLat([longitude, latitude]).addTo(map);
            }
            // Sort list by distance from the user
            partners.forEach((p) => {
              p._dist = (p.lat != null && p.lng != null)
                ? haversine(latitude, longitude, p.lat, p.lng) : Infinity;
            });
            const sorted = partners.slice().sort((a, b) => a._dist - b._dist);
            if (sorted[0] && isFinite(sorted[0]._dist)) focusPartner(sorted[0]);
          },
          () => { locateBtn.disabled = false; alert('Could not get your location.'); },
          { enableHighAccuracy: false, timeout: 8000 }
        );
      });
    } else if (locateBtn) {
      locateBtn.hidden = true;
    }

    // Mobile list/map toggle
    viewBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        viewBtns.forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        shell.dataset.view = btn.dataset.slView;
        if (btn.dataset.slView === 'map' && map) setTimeout(() => map.resize(), 50);
      });
    });
    shell.dataset.view = 'list';

    // ---- Init map ----
    if (!hasMapbox) {
      if (fallbackEl) fallbackEl.hidden = false;
      applyFilters(false);
      return;
    }

    try {
      mapboxgl.accessToken = token;
      map = new mapboxgl.Map({
        container: mapEl,
        style: styleUrl,
        center: [8, 30],
        zoom: 1.4,
        cooperativeGestures: true,
        attributionControl: true,
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      map.on('load', () => {
        mapReady = true;
        partners.forEach(addMarker);
        applyFilters(true);
        geocodeMissing();
      });
      map.on('error', (e) => { console.warn('[store-locator] map error', e && e.error); });
    } catch (err) {
      console.warn('[store-locator] map init failed', err);
      if (fallbackEl) fallbackEl.hidden = false;
      applyFilters(false);
    }

    // ---- Geocode partners without explicit coordinates ----
    async function geocodeMissing() {
      const missing = partners.filter((p) => (p.lat == null || p.lng == null) && p.geocodeQuery);
      if (!missing.length) return;
      const queue = missing.slice();
      const worker = async () => {
        while (queue.length) {
          const p = queue.shift();
          try {
            const coords = await geocode(p.geocodeQuery, token);
            if (coords) {
              p.lng = coords[0]; p.lat = coords[1];
              addMarker(p);
            }
          } catch (e) { /* skip */ }
        }
      };
      // Small concurrency to stay well within rate limits
      await Promise.all([worker(), worker(), worker()]);
      applyFilters(false);
    }
  }

  const geoCache = {};
  async function geocode(query, token) {
    const key = query.slice(0, 240);
    if (geoCache[key]) return geoCache[key];
    try {
      const cached = sessionStorage.getItem('sl-geo:' + key);
      if (cached) { geoCache[key] = JSON.parse(cached); return geoCache[key]; }
    } catch (e) { /* ignore */ }
    const url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
      encodeURIComponent(key) + '.json?limit=1&access_token=' + encodeURIComponent(token);
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const center = data && data.features && data.features[0] && data.features[0].center;
    if (!center) return null;
    geoCache[key] = center;
    try { sessionStorage.setItem('sl-geo:' + key, JSON.stringify(center)); } catch (e) { /* ignore */ }
    return center;
  }

  function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371, toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor: re-init when the section is re-rendered
  document.addEventListener('shopify:section:load', boot);
})();
